import React, { useEffect, useState } from 'react';
import * as http from '../../../../utils/http';
import * as translator from '../../../../utils/translator';
import { clientConfig } from '../../../../config';
import './ThankyouVerificationPage.css';

const bodyScanIcon = `${clientConfig.IMAGES_URL}uploads/icons/thankyou-body-scan.svg`;
const idDocIcon = `${clientConfig.IMAGES_URL}uploads/icons/thankyou-id-doc.svg`;
const warningIcon = `${clientConfig.IMAGES_URL}uploads/icons/thankyou-warning.svg`;
const checkIcon = `${clientConfig.IMAGES_URL}uploads/icons/thankyou-check.svg`;

interface Props {
  country: string;
  hashID: string;
  language: string;
  onNoPendingActions?: () => void;
}

export default function ThankyouVerificationPage({ country, hashID, language, onNoPendingActions }: Props) {
  const [needsPhotoUpload, setNeedsPhotoUpload] = useState(false);
  const [needsIdVerification, setNeedsIdVerification] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    http
      .getFromServer(`${country}/account/orders/${hashID}/pending-actions`, [http.MIDDLEWARES.CUSTOMER_ID])
      .then((res: any) => {
        if (cancelled) return;
        const photo = res.data.needsPhotoUpload === true;
        const id = res.data.needsIdVerification === true;
        setNeedsPhotoUpload(photo);
        setNeedsIdVerification(id);
        if (!photo && !id) {
          onNoPendingActions?.();
        }
      })
      .catch(() => {
        if (!cancelled) {
          onNoPendingActions?.();
        }
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [country, hashID, onNoPendingActions]);

  if (!loaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
        <div
          style={{
            width: 40,
            height: 40,
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #333',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }
  if (!needsPhotoUpload && !needsIdVerification) return null;

  const hasBoth = needsPhotoUpload && needsIdVerification;
  const isPhotoOnly = needsPhotoUpload && !needsIdVerification;
  const photoIndex = 1;
  const idIndex = needsPhotoUpload ? 2 : 1;
  const myAccountUrl = `/${country}/account/my-orders`;

  const t = (key: string) => translator.translate(language, 'thankyou', `verification/${key}`);

  const cardTitle = hasBoth
    ? t('card_title_both')
    : isPhotoOnly
    ? t('card_title_photo_only')
    : t('card_title_id_only');

  const uploadBtn = (className: string) => (
    <a href={myAccountUrl} className={`ty-v__upload-btn ${className}`}>
      {t('upload_btn')}
    </a>
  );

  return (
    <div className="ty-verification">
      <div className="ty-verification__hero">
        <div className="ty-verification__badge">
          <img src={checkIcon} alt="" className="ty-verification__check-icon" />
        </div>
        <h1 className="ty-verification__title">
          <span className="ty-verification__title-gradient">{t('hero_title_gradient')}</span>
          {t('hero_title_rest')}
        </h1>
        <p className="ty-verification__subtitle">{t('hero_subtitle')}</p>
      </div>

      <div className="ty-v__card">
        {/* Header row — Case 1 desktop: button on the right */}
        <div className="ty-v__header-row">
          <div className="ty-v__warning-wrap">
            <img src={warningIcon} alt="" className="ty-v__warning-icon" />
            <div className="ty-v__warning-text">
              <p className="ty-v__card-title">{cardTitle}</p>
              <p className="ty-v__card-subtitle">
                {t('card_subtitle')}
              </p>
            </div>
          </div>
          {hasBoth && uploadBtn('ty-v__btn--desktop')}
        </div>

        {/* Items grid — Cases 2/3 desktop: button sits beside the single item */}
        <div className={`ty-v__grid${hasBoth ? ' ty-v__grid--two' : ' ty-v__grid--single'}`}>
          {needsPhotoUpload && (
            <div className="ty-v__item">
              <div className="ty-v__item-icon-box">
                <img src={bodyScanIcon} alt="" className="ty-v__item-icon" />
              </div>
              <div className="ty-v__item-text">
                <p className="ty-v__item-title">{photoIndex}. {t('photo_item_title')}</p>
                <p className="ty-v__item-desc">
                  {hasBoth ? t('photo_item_desc_both') : t('photo_item_desc_photo_only')}
                </p>
              </div>
            </div>
          )}
          {needsIdVerification && (
            <div className="ty-v__item">
              <div className="ty-v__item-icon-box">
                <img src={idDocIcon} alt="" className="ty-v__item-icon" />
              </div>
              <div className="ty-v__item-text">
                <p className="ty-v__item-title">{idIndex}. {t('id_item_title')}</p>
                <p className="ty-v__item-desc">
                  {t('id_item_desc')}
                </p>
              </div>
            </div>
          )}
          {!hasBoth && uploadBtn('ty-v__btn--desktop')}
        </div>

        {/* Mobile only: full-width button below items */}
        {uploadBtn('ty-v__btn--mobile')}
      </div>

      <div className="ty-verification__steps-section">
        <h2 className="ty-verification__steps-title">{t('steps_title')}</h2>
        <div className="ty-verification__steps">
          <div className="ty-verification__step">
            <div className="ty-verification__step-num">1</div>
            <p className="ty-verification__step-title">{t('step1_title')}</p>
            <p className="ty-verification__step-desc">{t('step1_desc')}</p>
          </div>
          <div className="ty-verification__step">
            <div className="ty-verification__step-num">2</div>
            <p className="ty-verification__step-title">{t('step2_title')}</p>
            <p className="ty-verification__step-desc">{t('step2_desc')}</p>
          </div>
          <div className="ty-verification__step">
            <div className="ty-verification__step-num">3</div>
            <p className="ty-verification__step-title">{t('step3_title')}</p>
            <p className="ty-verification__step-desc">{t('step3_desc')}</p>
          </div>
        </div>
      </div>

      <p className="ty-verification__footer">{t('footer')}</p>
    </div>
  );
}
